import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loader2, Search } from "lucide-react";
import {
  timetableDataQualityApi,
  type DataQualityCategory,
  type TokenCleanupCategory,
  type TokenCleanupGroup,
  type TokenCleanupItem,
  type TokenCleanupSuggestion,
} from "@/services/api/timetableDataQuality";
import { extractErrorMessage } from "@/utils/errorHandler";
import { TokenPdfPanel } from "@/components/dataquality/TokenPdfPanel";
import { MultiSplitDialog } from "@/components/timetable-import/MultiSplitDialog";
import { SplitPartAssignPanel } from "@/components/timetable-import/SplitPartAssignPanel";
import { TokenFieldBadge } from "@/components/timetable-import/TokenFieldBadge";
import { UnmatchedSubjectDialog } from "@/components/timetable-import/UnmatchedSubjectDialog";
import type { PartFieldId, SplitPartGroup } from "@/utils/splitPartGroups";
import { canSplitToken, mergeSplitQueue } from "@/utils/tokenSplit";
import {
  invalidateIgnoredTokenNames,
  isIgnoredToken,
  knownIgnoredTokenNames,
  loadIgnoredTokenNames,
  rememberIgnoredTokens,
} from "@/utils/ignoredTokens";

const CATEGORY_LABELS: Record<TokenCleanupCategory, string> = {
  subject: "Fach",
  room: "Raum",
  klass: "Klasse",
  ignore: "Ignoriert",
};

const TOKEN_CATS = ["subject", "room", "klass", "ignore"] as const;
type TokenView = TokenCleanupCategory | "all";

const GROUP_TITLE: Record<TokenCleanupGroup, string> = {
  fach: "Sieht aus wie Fach",
  klass: "Sieht aus wie Klasse",
  raum: "Sieht aus wie Raum",
  ignore: "Füller",
  unused: "Ungenutzt",
  ok: "Bestätigt",
};

const GROUP_ORDER: TokenCleanupGroup[] = [
  "fach",
  "klass",
  "raum",
  "ignore",
  "unused",
  "ok",
];

const CATS: { id: DataQualityCategory; label: string; className: string }[] = [
  {
    id: "subject",
    label: "Fach",
    className: "bg-blue-700 text-white border-blue-700",
  },
  {
    id: "room",
    label: "Raum",
    className: "bg-purple-800 text-white border-purple-800",
  },
  {
    id: "klass",
    label: "Klasse",
    className: "bg-emerald-700 text-white border-emerald-700",
  },
];

const CAT_GHOST: Record<DataQualityCategory, string> = {
  subject: "text-blue-800 bg-blue-50 border-blue-200",
  room: "text-purple-800 bg-purple-50 border-purple-200",
  klass: "text-emerald-800 bg-emerald-50 border-emerald-200",
};

interface TokenCleanupTabProps {
  batchId: string | null;
  refreshTick?: number;
  onSuccess: (message: string, batchId: string | null) => void;
  onError: (message: string) => void;
  onRequestMemoryWipe?: () => void;
}

function otherCats(sug: TokenCleanupSuggestion): DataQualityCategory[] {
  return CATS.map((c) => c.id).filter((id) => id !== sug);
}

function splitOrigin(item: TokenCleanupItem): "auto" | "manual" | null {
  if (item.auto_split) return "auto";
  if (item.was_split || item.split_parts?.length) return "manual";
  return null;
}

function assignmentOpen(item: TokenCleanupItem): boolean {
  return (item.split_parts ?? []).some(
    (part) => !part.field || part.field === "open"
  );
}

export function TokenCleanupTab({
  batchId,
  refreshTick = 0,
  onSuccess,
  onError,
  onRequestMemoryWipe,
}: TokenCleanupTabProps) {
  const [category, setCategory] = useState<TokenView>("room");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState<TokenCleanupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pdfItem, setPdfItem] = useState<TokenCleanupItem | null>(null);
  const [splitGroups, setSplitGroups] = useState<SplitPartGroup[] | null>(null);
  const [multiSplitItems, setMultiSplitItems] = useState<
    TokenCleanupItem[] | null
  >(null);
  const [pendingSplits, setPendingSplits] = useState<Array<{
    item: TokenCleanupItem;
    parts: string[];
  }> | null>(null);
  const queuedSplits = useRef<
    Array<{ item: TokenCleanupItem; parts: string[] }>
  >([]);
  const [pendingSubject, setPendingSubject] = useState<{
    items: TokenCleanupItem[];
    index: number;
  } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const cats: TokenCleanupCategory[] =
        category === "all" ? [...TOKEN_CATS] : [category];
      const responses = await Promise.all(
        cats.map((cat) =>
          timetableDataQualityApi.getTokens({
            category: cat,
            search: debounced || undefined,
          })
        )
      );
      const seen = new Set<string>();
      const merged: TokenCleanupItem[] = [];
      for (const response of responses) {
        for (const item of response.items) {
          if (seen.has(item.id)) continue;
          seen.add(item.id);
          merged.push(item);
        }
      }
      setItems(merged);
      setSelected(new Set());
    } catch (err) {
      onError(
        extractErrorMessage(err, {
          loadError: "Tokens konnten nicht geladen werden",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [category, debounced, onError]);

  useEffect(() => {
    void fetchTokens();
  }, [fetchTokens, refreshTick]);

  const grouped = useMemo(() => {
    const by: Partial<Record<TokenCleanupGroup, TokenCleanupItem[]>> = {};
    for (const item of items) {
      const list = by[item.group] ?? [];
      list.push(item);
      by[item.group] = list;
    }
    return GROUP_ORDER.filter((g) => (by[g] ?? []).length > 0).map((g) => ({
      group: g,
      items: by[g] ?? [],
    }));
  }, [items]);

  const splitCounts = useMemo(() => {
    let auto = 0;
    let manual = 0;
    let open = 0;
    for (const item of items) {
      if (assignmentOpen(item)) {
        open += 1;
        continue;
      }
      const origin = splitOrigin(item);
      if (origin === "auto") auto += 1;
      else if (origin === "manual") manual += 1;
    }
    return { auto, manual, open };
  }, [items]);

  type TokenAction = "recategorize" | "ignore" | "confirm" | "delete" | "split";

  const closeSplit = () => {
    queuedSplits.current = [];
    setSplitGroups(null);
    setMultiSplitItems(null);
    setPendingSplits(null);
  };

  const apply = async (
    item: TokenCleanupItem,
    action: TokenAction,
    target?: DataQualityCategory,
    parts?: { token: string; field: string }[],
    canonicalId?: string
  ) => {
    if (busyId) return false;
    if (action === "recategorize" && target === "subject" && !canonicalId) {
      setPendingSubject({ items: [item], index: 0 });
      return false;
    }
    if (
      (item.category === "ignore" || category === "ignore") &&
      action !== "delete" &&
      action !== "recategorize" &&
      action !== "split"
    )
      return false;
    if (action === "recategorize" && target === item.category)
      action = "confirm";
    setBusyId(item.id);
    try {
      const result = await timetableDataQualityApi.applyToken({
        category: item.category,
        catalog_id: item.id,
        action,
        target_category: target,
        parts,
        canonical_id: canonicalId,
      });
      if (action === "ignore") {
        rememberIgnoredTokens([item.name]);
        invalidateIgnoredTokenNames();
      }
      if (action === "confirm") {
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id
              ? { ...row, group: "ok", suggestion: item.category }
              : row
          )
        );
        onSuccess(`«${item.name}» bestätigt`, result.batch_id);
      } else if (action === "delete") {
        onSuccess(
          result.skipped_conflicts
            ? `«${item.name}» behalten (Qualifikationsmatrix)`
            : `«${item.name}» gelöscht`,
          result.batch_id
        );
        await fetchTokens();
      } else if (action === "split") {
        closeSplit();
        const extra = result.skipped_conflicts
          ? ` · ${result.skipped_conflicts} Konflikte übersprungen`
          : "";
        onSuccess(`«${item.name}» geteilt${extra}`, result.batch_id);
        await fetchTokens();
      } else {
        setItems((prev) => prev.filter((row) => row.id !== item.id));
        const label =
          action === "ignore"
            ? "ignoriert"
            : `→ ${CATEGORY_LABELS[target as DataQualityCategory]}`;
        const extra = result.skipped_conflicts
          ? ` · ${result.skipped_conflicts} Konflikte übersprungen`
          : "";
        onSuccess(`«${item.name}» ${label}${extra}`, result.batch_id);
      }
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      return true;
    } catch (err) {
      onError(
        extractErrorMessage(err, {
          loadError: "Token konnte nicht umgehängt werden",
        })
      );
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const applySelected = async (
    action: TokenAction,
    target?: DataQualityCategory,
    canonicalId?: string
  ) => {
    if (busyId || selected.size === 0) return;
    if (
      category === "ignore" &&
      action !== "delete" &&
      action !== "recategorize"
    )
      return;
    if (action === "recategorize" && category !== "all" && target === category)
      action = "confirm";
    if (action === "recategorize" && target === "subject" && !canonicalId) {
      const chosen = items.filter((row) => selected.has(row.id));
      if (chosen.length === 0) return;
      setPendingSubject({ items: chosen, index: 0 });
      return;
    }
    const chosen = items.filter((row) => selected.has(row.id));
    if (chosen.length === 0) return;
    const byKey = new Map<
      string,
      { category: TokenCleanupCategory; action: TokenAction; ids: string[] }
    >();
    for (const row of chosen) {
      let rowAction = action;
      if (
        row.category === "ignore" &&
        rowAction !== "delete" &&
        rowAction !== "recategorize"
      )
        continue;
      if (rowAction === "ignore" && row.category === "ignore") continue;
      if (rowAction === "recategorize" && target === row.category)
        rowAction = "confirm";
      const key = `${row.category}:${rowAction}`;
      const bucket = byKey.get(key) ?? {
        category: row.category,
        action: rowAction,
        ids: [],
      };
      bucket.ids.push(row.id);
      byKey.set(key, bucket);
    }
    const calls = [...byKey.values()];
    if (calls.length === 0) return;
    setBusyId("bulk");
    try {
      let batchId: string | null = null;
      let skipped = 0;
      const gone = new Set<string>();
      const confirmed = new Map<string, TokenCleanupCategory>();
      for (const call of calls) {
        const result = await timetableDataQualityApi.applyToken({
          category: call.category,
          catalog_ids: call.ids,
          action: call.action,
          target_category: call.action === "recategorize" ? target : undefined,
          canonical_id: canonicalId,
        });
        batchId = result.batch_id;
        skipped += result.skipped_conflicts;
        if (call.action === "ignore") {
          rememberIgnoredTokens(
            chosen
              .filter((row) => call.ids.includes(row.id))
              .map((row) => row.name)
          );
          invalidateIgnoredTokenNames();
        }
        if (call.action === "confirm") {
          for (const id of call.ids) confirmed.set(id, call.category);
        } else if (call.action !== "delete") {
          for (const id of call.ids) gone.add(id);
        }
      }
      if (action === "delete") {
        const kept = skipped;
        const removed = Math.max(chosen.length - kept, 0);
        const text =
          kept && removed
            ? `${removed} gelöscht · ${kept} behalten (Qualifikationsmatrix)`
            : kept
              ? `${kept} behalten (Qualifikationsmatrix)`
              : `${chosen.length} Tokens gelöscht`;
        onSuccess(text, batchId);
        await fetchTokens();
      } else {
        if (confirmed.size > 0) {
          setItems((prev) =>
            prev.map((row) =>
              confirmed.has(row.id)
                ? {
                    ...row,
                    group: "ok",
                    suggestion: confirmed.get(row.id) ?? row.category,
                  }
                : row
            )
          );
        }
        if (gone.size > 0)
          setItems((prev) => prev.filter((row) => !gone.has(row.id)));
        const label =
          action === "confirm"
            ? "bestätigt"
            : action === "ignore"
              ? "ignoriert"
              : `→ ${CATEGORY_LABELS[target as DataQualityCategory]}`;
        const extra = skipped ? ` · ${skipped} Konflikte übersprungen` : "";
        onSuccess(`${chosen.length} Tokens ${label}${extra}`, batchId);
        setSelected(new Set());
      }
    } catch (err) {
      onError(
        extractErrorMessage(err, {
          loadError: "Tokens konnten nicht umgehängt werden",
        })
      );
    } finally {
      setBusyId(null);
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected =
    items.length > 0 && items.every((row) => selected.has(row.id));
  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(items.map((row) => row.id)));
  };

  const applySplitAssignments = async (
    assignments: Array<{ token: string; field: PartFieldId }>
  ) => {
    const fieldOf = new Map(assignments.map((row) => [row.token, row.field]));
    if (pendingSplits && pendingSplits.length > 0) {
      const payloads: Array<{
        item: TokenCleanupItem;
        parts: { token: string; field: string }[];
      }> = [];
      for (const row of pendingSplits) {
        const parts = row.parts
          .map((token) => {
            const field = fieldOf.get(token);
            return field ? { token, field } : null;
          })
          .filter(
            (part): part is { token: string; field: PartFieldId } =>
              part !== null
          );
        if (parts.length < 2) {
          onError("Mindestens zwei Teile zuordnen");
          return;
        }
        rememberIgnoredTokens(
          parts
            .filter((part) => part.field === "ignore")
            .map((part) => part.token)
        );
        payloads.push({ item: row.item, parts });
      }
      if (busyId) return;
      setBusyId("bulk-split");
      try {
        let batchId: string | null = null;
        let skipped = 0;
        for (const { item, parts } of payloads) {
          const result = await timetableDataQualityApi.applyToken({
            category: item.category,
            catalog_id: item.id,
            action: "split",
            parts,
          });
          batchId = result.batch_id;
          skipped += result.skipped_conflicts;
        }
        closeSplit();
        const extra = skipped ? ` · ${skipped} Konflikte übersprungen` : "";
        onSuccess(`${payloads.length} Tokens geteilt${extra}`, batchId);
        setSelected(new Set());
        await fetchTokens();
      } catch (err) {
        onError(
          extractErrorMessage(err, {
            loadError: "Token konnte nicht umgehängt werden",
          })
        );
      } finally {
        setBusyId(null);
      }
      return;
    }
    onError("Mindestens zwei Teile zuordnen");
  };

  const openSplit = (rows: TokenCleanupItem[]) => {
    if (busyId) return;
    const usable = rows.filter((row) => canSplitToken(row.name));
    if (usable.length === 0) return;
    setSplitGroups(null);
    setPendingSplits(null);
    setMultiSplitItems(usable);
  };

  const toggleGroup = (rows: TokenCleanupItem[]) => {
    const ids = rows.map((row) => row.id);
    const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCategory("all")}
            className={`rounded-full border px-3 py-1.5 text-sm ${
              category === "all"
                ? "border-gray-900 bg-gray-900 text-white"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Alle
          </button>
          {TOKEN_CATS.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`rounded-full border px-3 py-1.5 text-sm ${
                category === cat
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Token suchen…"
              className="w-56 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <p>
            {items.length}{" "}
            {category === "all" ? "Tokens" : CATEGORY_LABELS[category]}
            {category === "room" ? "e" : ""}
            {category === "ignore" ? "" : " · gruppiert nach Aussehen"}
          </p>
          {(category === "ignore" || category === "all") &&
          (splitCounts.auto > 0 ||
            splitCounts.manual > 0 ||
            splitCounts.open > 0) ? (
            <p className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">
                {splitCounts.auto} autogesplittet
              </span>
              <span
                className="rounded bg-slate-200 px-2 py-0.5 font-semibold text-slate-700"
                title="Bereits entschieden, keine Aktion nötig"
              >
                {splitCounts.manual} manuell gesplittet
              </span>
              {splitCounts.open > 0 ? (
                <span
                  className="rounded bg-rose-100 px-2 py-0.5 font-semibold text-rose-800"
                  title="Teil ohne Feld, Zuordnung nötig"
                >
                  {splitCounts.open} Zuordnung offen
                </span>
              ) : null}
            </p>
          ) : null}
          {onRequestMemoryWipe ? (
            <button
              type="button"
              onClick={onRequestMemoryWipe}
              className="ml-auto px-3 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
            >
              Token-Speicher löschen
            </button>
          ) : null}
        </div>
      </div>
      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white">
          <span>{selected.size} gewählt</span>
          {category === "all" ? (
            <button
              type="button"
              disabled={busyId !== null}
              onClick={() => applySelected("confirm")}
              aria-label="Auswahl bestätigen"
              className="rounded border border-white/30 bg-white px-2 py-1 text-xs font-semibold text-gray-900 disabled:opacity-40"
            >
              Bestätigen
            </button>
          ) : null}
          {CATS.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={busyId !== null}
              onClick={() =>
                c.id === category
                  ? applySelected("confirm")
                  : applySelected("recategorize", c.id)
              }
              aria-label={
                c.id === category
                  ? "Auswahl bestätigen"
                  : `Auswahl als ${c.label}`
              }
              className="rounded border border-white/30 bg-white px-2 py-1 text-xs font-semibold text-gray-900 disabled:opacity-40"
            >
              {c.id === category ? "Bestätigen" : c.label}
            </button>
          ))}
          {category !== "ignore" ? (
            <button
              type="button"
              disabled={busyId !== null}
              onClick={() => applySelected("ignore")}
              aria-label="Auswahl ignorieren"
              className="rounded border border-white/30 bg-white px-2 py-1 text-xs font-semibold text-gray-900 disabled:opacity-40"
            >
              Ignorieren
            </button>
          ) : null}
          <button
            type="button"
            disabled={busyId !== null}
            onClick={() =>
              openSplit(items.filter((row) => selected.has(row.id)))
            }
            aria-label="Auswahl teilen"
            className="rounded border border-white/30 bg-white px-2 py-1 text-xs font-semibold text-gray-900 disabled:opacity-40"
          >
            Teilen
          </button>
          <button
            type="button"
            disabled={busyId !== null}
            onClick={() => applySelected("delete")}
            aria-label="Auswahl löschen"
            className="rounded border border-red-300 bg-red-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-40"
          >
            Löschen
          </button>
        </div>
      ) : null}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center p-8 text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Lade Tokens…
          </div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">
            Keine Tokens in dieser Kategorie.
          </p>
        ) : (
          <table className="w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Alle Tokens auswählen"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Token
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Nutzung
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-700" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grouped.map(({ group, items: rows }) => (
                <Fragment key={group}>
                  {category !== "ignore" ? (
                    <tr>
                      <td className="bg-gray-100 px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`${GROUP_TITLE[group]} auswählen`}
                          checked={
                            rows.length > 0 &&
                            rows.every((row) => selected.has(row.id))
                          }
                          onChange={() => toggleGroup(rows)}
                        />
                      </td>
                      <td
                        colSpan={3}
                        className="bg-gray-100 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-500"
                      >
                        {GROUP_TITLE[group]} · {rows.length}
                      </td>
                    </tr>
                  ) : null}
                  {rows.map((item) => {
                    const origin = splitOrigin(item);
                    const open = assignmentOpen(item);
                    return (
                      <tr
                        key={item.id}
                        className={
                          selected.has(item.id)
                            ? "bg-blue-50"
                            : open
                              ? "bg-rose-50"
                              : origin === "auto"
                                ? "bg-amber-50"
                                : origin === "manual"
                                  ? "bg-slate-50"
                                  : ""
                        }
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            aria-label={`${item.name} auswählen`}
                            checked={selected.has(item.id)}
                            onChange={() => toggle(item.id)}
                          />
                        </td>
                        <td className="px-4 py-3 font-mono">
                          {item.name}
                          {origin === "auto" ? (
                            <span
                              className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-amber-800"
                              title="Automatisch geteilt, Original ignoriert"
                            >
                              Autogesplittet
                            </span>
                          ) : null}
                          {origin === "manual" && !open ? (
                            <span
                              className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-slate-700"
                              title="Manuell geteilt. Bereits entschieden, keine Aktion nötig."
                            >
                              Manuell gesplittet
                            </span>
                          ) : null}
                          {open ? (
                            <span
                              className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-rose-800"
                              title="Teil ohne Feld. Zuordnung nötig."
                            >
                              Zuordnung offen
                            </span>
                          ) : null}
                          {item.split_of ? (
                            <span
                              className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-slate-700"
                              title="Ignorierter Teil eines Splits"
                            >
                              Teil von {item.split_of}
                            </span>
                          ) : null}
                          {category === "all" ? (
                            <span className="ml-2 font-sans text-xs text-gray-500">
                              {CATEGORY_LABELS[item.category]}
                            </span>
                          ) : null}
                          {item.split_parts?.length ? (
                            <div
                              className="mt-1 flex flex-wrap items-center gap-1 font-sans"
                              title="geteilt"
                            >
                              {item.split_parts.map((part) => (
                                <span
                                  key={`${part.token}:${part.field}`}
                                  className="inline-flex items-center gap-1"
                                >
                                  <code className="font-mono text-[10px] text-gray-700">
                                    {part.token}
                                  </code>
                                  <TokenFieldBadge
                                    field={
                                      part.field && part.field !== "open"
                                        ? part.field
                                        : "open"
                                    }
                                  />
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-0.5 font-sans text-xs text-gray-400">
                              {item.alias_of
                                ? `Alias von ${item.alias_of.name} (${CATEGORY_LABELS[item.alias_of.category]})`
                                : null}
                              {item.alias_of && item.teachers.length > 0
                                ? " · "
                                : null}
                              {item.teachers.length > 0
                                ? item.teachers
                                    .slice(0, 3)
                                    .map((t) => t.name)
                                    .join(", ")
                                : item.alias_of
                                  ? null
                                  : "—"}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {item.entry_count} · {item.teacher_count} LP
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            {item.category === "ignore" ? (
                              <>
                                {CATS.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    disabled={busyId !== null}
                                    onClick={() =>
                                      apply(item, "recategorize", c.id)
                                    }
                                    aria-label={`Als ${c.label}`}
                                    className={`rounded border px-2 py-1 text-xs ${CAT_GHOST[c.id]} disabled:opacity-50`}
                                  >
                                    {c.label}
                                  </button>
                                ))}
                                <button
                                  type="button"
                                  disabled={busyId !== null}
                                  onClick={() => openSplit([item])}
                                  aria-label="Teilen"
                                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                                >
                                  Teilen
                                </button>
                                <button
                                  type="button"
                                  disabled={busyId !== null}
                                  onClick={() => apply(item, "delete")}
                                  aria-label="Löschen"
                                  className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-semibold text-red-700 disabled:opacity-50"
                                >
                                  Löschen
                                </button>
                              </>
                            ) : item.suggestion === "ignore" ? (
                              <button
                                type="button"
                                disabled={busyId !== null}
                                onClick={() => apply(item, "ignore")}
                                aria-label="Ignorieren"
                                className="rounded border border-gray-600 bg-gray-600 px-2 py-1 text-xs font-bold text-white disabled:opacity-50"
                              >
                                Ignorieren
                              </button>
                            ) : (
                              <>
                                {item.group === "ok" ? (
                                  <span className="text-xs text-gray-400">
                                    bestätigt
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={busyId !== null}
                                    onClick={() => apply(item, "confirm")}
                                    aria-label="Bestätigen"
                                    className="rounded border border-gray-900 bg-gray-900 px-2 py-1 text-xs font-bold text-white disabled:opacity-50"
                                  >
                                    Bestätigen
                                  </button>
                                )}
                                {item.suggestion !== item.category
                                  ? CATS.filter(
                                      (c) => c.id === item.suggestion
                                    ).map((c) => (
                                      <button
                                        key={c.id}
                                        type="button"
                                        disabled={busyId !== null}
                                        onClick={() =>
                                          apply(item, "recategorize", c.id)
                                        }
                                        aria-label={`Als ${c.label}`}
                                        className={`rounded border px-2 py-1 text-xs font-bold ${c.className} disabled:opacity-50`}
                                      >
                                        {c.label}
                                      </button>
                                    ))
                                  : null}
                                {otherCats(item.suggestion)
                                  .filter((id) => id !== item.category)
                                  .map((id) => (
                                    <button
                                      key={id}
                                      type="button"
                                      disabled={busyId !== null}
                                      onClick={() =>
                                        apply(item, "recategorize", id)
                                      }
                                      aria-label={`Als ${CATEGORY_LABELS[id]}`}
                                      className={`rounded border px-2 py-1 text-xs ${CAT_GHOST[id]} disabled:opacity-50`}
                                    >
                                      {CATEGORY_LABELS[id]}
                                    </button>
                                  ))}
                                <button
                                  type="button"
                                  disabled={busyId !== null}
                                  onClick={() => apply(item, "ignore")}
                                  aria-label="Ignorieren"
                                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                                >
                                  Ignorieren
                                </button>
                                <button
                                  type="button"
                                  disabled={busyId !== null}
                                  onClick={() => openSplit([item])}
                                  aria-label="Teilen"
                                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                                >
                                  Teilen
                                </button>
                              </>
                            )}
                            {item.category !== "ignore" ? (
                              <button
                                type="button"
                                disabled={item.teachers.length === 0}
                                onClick={() => setPdfItem(item)}
                                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-blue-700 disabled:opacity-40"
                              >
                                PDF
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {pdfItem ? (
        <TokenPdfPanel
          tokenName={pdfItem.name}
          teachers={pdfItem.teachers}
          batchId={batchId}
          onClose={() => setPdfItem(null)}
        />
      ) : null}
      {multiSplitItems && !splitGroups ? (
        <MultiSplitDialog
          isOpen
          tokens={multiSplitItems.map((row) => row.name)}
          onApply={(results, options) => {
            const byName = new Map(
              multiSplitItems.map((row) => [row.name, row])
            );
            const pending = results
              .filter((row) => row.parts.length >= 2)
              .map((row) => {
                const item = byName.get(row.token);
                return item ? { item, parts: row.parts } : null;
              })
              .filter(
                (row): row is { item: TokenCleanupItem; parts: string[] } =>
                  row !== null
              );
            const ready = mergeSplitQueue(
              queuedSplits.current,
              pending,
              options?.revoke,
              (row) => [row.item.name]
            );
            if (options?.close === false) {
              queuedSplits.current = ready;
              return;
            }
            queuedSplits.current = [];
            if (ready.length === 0) {
              closeSplit();
              return;
            }
            void (async () => {
              const names = await loadIgnoredTokenNames().catch(() =>
                knownIgnoredTokenNames()
              );
              const groups: SplitPartGroup[] = ready.map((row) => ({
                parent: row.item.name,
                parts: row.parts.map((token) => ({
                  token,
                  nested: false,
                  assignable: true,
                  field: isIgnoredToken(token, names) ? "ignore" : null,
                })),
              }));
              if (
                groups.every((group) =>
                  group.parts.every((part) => part.field === "ignore")
                )
              ) {
                await applySplitAssignments(
                  groups.flatMap((group) =>
                    group.parts.map((part) => ({
                      token: part.token,
                      field: "ignore" as const,
                    }))
                  )
                );
                return;
              }
              setPendingSplits(ready);
              setSplitGroups(groups);
            })();
          }}
          onCancel={closeSplit}
        />
      ) : null}
      {pendingSplits && splitGroups ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-3 py-2 text-sm font-semibold">
              Teile zuordnen ·{" "}
              {pendingSplits.length === 1
                ? pendingSplits[0]!.item.name
                : `${pendingSplits.length} Token`}
            </div>
            <SplitPartAssignPanel
              groups={splitGroups}
              onApply={(assignments) => void applySplitAssignments(assignments)}
              onCancel={closeSplit}
              onSplit={() => {
                setSplitGroups(null);
                setPendingSplits(null);
              }}
              canSplit
            />
          </div>
        </div>
      ) : null}
      {pendingSubject && pendingSubject.items[pendingSubject.index] ? (
        <UnmatchedSubjectDialog
          key={pendingSubject.items[pendingSubject.index]!.id}
          open
          token={pendingSubject.items[pendingSubject.index]!.name}
          hasQualificationMatrix
          onResolve={async (_type, data) => {
            const canonicalId = String(
              data.canonical_subject_id || data.subject_id || ""
            );
            const pending = pendingSubject;
            const item = pending.items[pending.index];
            setPendingSubject(null);
            if (!pending || !canonicalId || !item) return;
            const ok = await apply(
              item,
              "recategorize",
              "subject",
              undefined,
              canonicalId
            );
            const next = pending.index + 1;
            if (ok && next < pending.items.length) {
              setPendingSubject({ items: pending.items, index: next });
            }
          }}
          onClose={() => setPendingSubject(null)}
        />
      ) : null}
    </div>
  );
}
